import React from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'

/** Gap between the icon and the tooltip, and the minimum breathing room at the viewport edge. */
const OFFSET = 6
const EDGE_PADDING = 8

/**
 * Hover tooltip for an inline ⓘ icon.
 *
 * The bubble is portalled to <body> and positioned with `fixed` coordinates
 * measured off the icon. Anchoring it absolutely inside the trigger meant any
 * ancestor with `overflow: hidden` — every `.modal-container`, for one — sliced
 * the text off mid-sentence. The left edge is also clamped to the viewport so a
 * trigger near the right-hand side shifts the bubble left instead of clipping.
 */
export const InfoTooltip: React.FC<{ text: React.ReactNode; maxWidth?: number }> = ({
  text,
  maxWidth = 260,
}) => {
  const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null)
  const triggerRef = React.useRef<HTMLSpanElement>(null)
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const show = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    // Clamp against maxWidth: the bubble has not rendered yet, so its real width
    // is unknown and this is the widest it can be.
    // Use clientWidth to exclude the scrollbar, and assume border-box.
    const viewportWidth = document.documentElement.clientWidth
    const maxLeft = viewportWidth - maxWidth - EDGE_PADDING
    setPosition({
      top: rect.bottom + OFFSET,
      left: Math.max(EDGE_PADDING, Math.min(rect.left, maxLeft)),
    })
  }, [maxWidth])

  const hide = React.useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setPosition(null)
    }, 200)
  }, [])

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [])

  return (
    <span
      ref={triggerRef}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: '4px',
        verticalAlign: '-2px',
      }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <Info
        size={13}
        style={{
          color: position ? 'var(--primary, #6366f1)' : 'var(--text-muted, #94a3b8)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
        aria-label="More information"
      />
      {position &&
        typeof document !== 'undefined' &&
        createPortal(
          <span
            role="tooltip"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: 'max-content',
              maxWidth,
              padding: '8px 10px',
              background: 'var(--surface, #fff)',
              color: 'var(--text, #1e1e2e)',
              fontSize: '11px',
              fontWeight: 400,
              lineHeight: 1.5,
              border: '1px solid var(--border)',
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              /* Above .confirm-dialog-overlay (10000) so it is never buried by a modal. */
              zIndex: 10001,
              whiteSpace: 'pre-wrap',
              pointerEvents: 'none',
              boxSizing: 'border-box',
            }}
          >
            {text}
          </span>,
          document.body,
        )}
    </span>
  )
}
